import { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useField } from 'react-final-form';
import isEqual from 'lodash/isEqual';
import { useHistory } from 'react-router-dom';
import { useLocation } from 'react-router';
import qs from 'query-string';
import omit from 'lodash/omit';
import Rollbar from 'rollbar';

// Hooks
import { useOrganization } from './hooks/use-organization';

// Actions
import { getMembersAction } from 'Shared/actions/members';
import { getClientSideSearchData } from 'Shared/actions/clientSideSearchData';
import { getSyncStatus, getRecurringMeetings, loadAsanaData } from 'Shared/actions/user';
import { getWorkspaces } from 'Shared/actions/workspaces';

// Helpers
import { showNotificationError } from 'Shared/helpers';

// Selectors
import { getHealthCheckData, getWorkspaceByUuid } from 'Shared/selectors/workspace';
import { getAllMembers } from 'Shared/selectors/Members.selector';

// Types
import { Member } from 'Shared/types/Member.type';
import { BillingPlan, PlatformFeatureNameType, UserManagedFeatureNameType } from 'Shared/types/Organization.type';

declare global {
  interface Window {
    Rollbar: Rollbar;
    pendo?: {
      identify: (data: { visitor: any; account: any }) => void;
    };
  }
}

function logRollbarError(errorMessage: string, errorObject: any) {
  const rollbar = window.Rollbar as Rollbar;
  if (rollbar) rollbar.error(errorMessage, errorObject);
  return window.Rollbar;
}

export function useClientSideSearchData({ startFetch = true } = {}) {
  const dispatch = useDispatch();
  const { data: clientSideSearchData, wasRequested } = useSelector(state => state.clientSideSearchData);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    async function fetchAdditionalData() {
      setIsLoading(true);
      await dispatch(getClientSideSearchData());
      setIsLoading(false);
    }
    if (startFetch && !wasRequested) fetchAdditionalData();
  }, [startFetch, wasRequested, dispatch]);

  return { clientSideSearchData, isLoading };
}

export function useDeepEqualSelector(selector) {
  return useSelector(selector, isEqual);
}

export function useDeepCompareMemoize(value) {
  const ref = useRef();
  if (!isEqual(value, ref.current)) ref.current = value;
  return ref.current;
}

export function useDidUpdateEffect(func, deps) {
  const didMount = useRef(false);

  useEffect(() => {
    let unmount;
    if (didMount.current) unmount = func();
    else didMount.current = true;
    return unmount;
  }, deps);
}

export function useDropdownOnHover({ onClose }) {
  const timeoutRef = useRef<NodeJS.Timeout | undefined>();

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  });

  const handleMouseEnter = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  const handleMouseLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      if (timeoutRef.current) onClose();
    }, 100);
  }, [onClose]);

  return { handleMouseEnter, handleMouseLeave };
}

export function useFieldValue(fieldName) {
  return useField(fieldName, { subscription: { value: true } }).input.value;
}

export function useFieldChange(fieldName) {
  return useField(fieldName, { subscription: { value: true } }).input.onChange;
}

let getStatusTimeoutId;

export function useWaitForCalendarWorkspacesSynced({
  syncStatusRequestCondition = true,
  queryParams = {},
}: { syncStatusRequestCondition?: boolean; queryParams?: any } = {}): boolean {
  const dispatch = useDispatch();
  const calendarWorkspacesSynced = useSelector(state => state.syncStatus?.calendarWorkspacesSynced || false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchRecurringMeetings() {
      await dispatch(getRecurringMeetings(queryParams));
      setIsLoading(false);
    }

    if (syncStatusRequestCondition) {
      if (!calendarWorkspacesSynced) {
        dispatch(getSyncStatus());
        getStatusTimeoutId = setInterval(() => dispatch(getSyncStatus()), 3000);
      } else {
        clearInterval(getStatusTimeoutId);
        fetchRecurringMeetings();
      }
    } else fetchRecurringMeetings();

    return () => clearInterval(getStatusTimeoutId);
  }, [calendarWorkspacesSynced]);

  return isLoading;
}

export function useDeepCompareEffect(callback, dependencies) {
  useEffect(callback, useDeepCompareMemoize(dependencies));
}

interface UseMembersProps {
  refresh?: boolean;
  onDemand?: boolean;
}

export function useMembers({ refresh = false, onDemand = false }: UseMembersProps = {}) {
  const dispatch = useDispatch();
  const members: Member[] = useSelector(state => getAllMembers(state));
  const membersCount = members?.length;

  const [isLoading, setIsLoading] = useState(false);

  const fetchMembers = useCallback(async () => {
    try {
      setIsLoading(true);
      await dispatch(getMembersAction());
    } catch (e) {
      showNotificationError(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if ((!membersCount && !onDemand) || refresh) fetchMembers();
  }, [fetchMembers]);

  return { isLoading, members, fetchMembers };
}

export function useWindowSize() {
  const isClient = typeof window === 'object';

  function getSize() {
    return {
      width: isClient ? window?.innerWidth : undefined,
      height: isClient ? window?.innerHeight : undefined,
    };
  }

  const [windowSize, setWindowSize] = useState(getSize);

  useEffect(() => {
    if (!isClient) {
      return undefined;
    }

    function handleResize() {
      setWindowSize(getSize());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
}

export function useLocalStorage(key, initialValue?) {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState(() => {
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key);
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      return initialValue;
    }
  });

  useDidUpdateEffect(() => {
    try {
      // Save to local storage
      window.localStorage.setItem(key, JSON.stringify(storedValue));
    } catch (error) {
      logRollbarError('Error saving to local storage', error);
    }
  }, [storedValue]);

  return [storedValue, setStoredValue];
}

export const useToggle = (initial: boolean): [boolean, (value?: boolean) => void] => {
  const [toggleStatus, setToggleStatus] = useState(initial);

  const toggle = useCallback((value?: boolean) => {
    if (typeof value === 'boolean') {
      // If a boolean value is provided, set that specific value
      setToggleStatus(value);
    } else {
      // Otherwise toggle the current state
      setToggleStatus(state => !state);
    }
  }, []);

  return [toggleStatus, toggle];
};

export const useOneClick = ({ onClick }) => {
  const didClick = useRef(false);

  const handleOneClick = useCallback(
    event => {
      if (!didClick.current) {
        onClick(event);
        didClick.current = true;
      }
    },
    [onClick]
  );

  return [handleOneClick];
};

// Fetches asana data if not loaded and stores in redux
export const useAsanaData = () => {
  const dispatch = useDispatch();
  const asanaData = useSelector(state => state.user.asanaData);

  useEffect(() => {
    if (!asanaData) {
      dispatch(loadAsanaData());
    }
  }, [asanaData, dispatch]);

  return asanaData;
};

export const useFeature = (feature: UserManagedFeatureNameType | PlatformFeatureNameType) => {
  const { features } = useOrganization();
  const featureState = features[feature]?.state;

  return {
    isFreePlan: featureState === BillingPlan.freePlan,
    isPaidPlan: featureState === BillingPlan.paidPlan,
    isFreeTrial: featureState === BillingPlan.freeTrial,
    isActive: featureState === 'active',
    isDisabled: featureState === 'disabled',
  };
};

export const useWorkspaceHealthCheck = ({ workspaceId }) => {
  const localHealthCheckData = useSelector(state => getHealthCheckData(state));
  const [remoteHealthCheckData, setRemoteHealthCheckData] = useState(null);
  const [failedHealthChecks, setFailedHealthChecks] = useState(0);

  // Only listen to changes in the remote health data and run the test if it changed.
  // Report to rollbar if 3 checks in a row failed.  I originally was just returning the health
  // check as a function to use from this hook but was having issues due to the action cable
  // callbacks creating a closure over the localHealthCheckData and it would never update. This
  // way feels kinda wonky but works as intended.
  useEffect(() => {
    if (remoteHealthCheckData && isEqual(localHealthCheckData, remoteHealthCheckData)) {
      setFailedHealthChecks(0);
    } else if (remoteHealthCheckData) {
      if (failedHealthChecks === 2) {
        const errorObject = { workspaceId, remoteHealthCheckData, localHealthCheckData };
        logRollbarError('Workspace health check failed', errorObject);
      }
      setFailedHealthChecks(failedHealthChecks + 1);
    }
  }, [remoteHealthCheckData]); // eslint-disable-line react-hooks/exhaustive-deps

  return setRemoteHealthCheckData;
};

export function useQueryParam(param: string): [string | string[], (string) => void] {
  const location = useLocation();
  const history = useHistory();
  const queryParams = qs.parse(location.search);
  const paramValue = queryParams[param];

  const setParam = (value: string | boolean) => {
    if (value == null) {
      // if passed in null, remove query param
      history.push(
        qs.stringifyUrl({
          url: history.location.pathname,
          query: omit(queryParams, param),
        })
      );
    } else {
      // if passed in actual value, set it
      history.push(qs.stringifyUrl({ url: history.location.pathname, query: { ...queryParams, [param]: value } }));
    }
  };

  return [paramValue, setParam];
}

export function useGoalPermissionsLabel(goal) {
  const workspace = useSelector(state => getWorkspaceByUuid(goal?.workspaceSlug)(state));
  if (!goal) return '';
  switch (goal.privacySetting) {
    case 'justMe':
      return 'Just Me';
    case 'everyone':
      return 'Everyone';
    case 'groupWorkspace':
    case 'meAndChoosenTeammate':
      return workspace.name;
    default:
      return workspace.name;
  }
}

export function useFetchWorkspaces() {
  const dispatch = useDispatch();
  const [isLoading, toggleIsLoading] = useToggle(false);

  useEffect(() => {
    async function fetchWorkspaces() {
      try {
        toggleIsLoading();
        await dispatch(getWorkspaces());
      } catch (e) {
        showNotificationError(e);
      } finally {
        toggleIsLoading();
      }
    }

    fetchWorkspaces();
  }, []);

  return [isLoading];
}
